import { getUploadImageKey } from '@onefootprint/core';
import { postEntitiesByFpIdVaultDecrypt } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { getLogger } from '@onefootprint/logger';
import type { DataIdentifier, Document, DocumentKind, Entity } from '@onefootprint/request-types/dashboard';
import { useEffect } from 'react';
import { create } from 'zustand';
import type { VaultType } from '@/entities/hooks/use-entity-vault';
import getDocumentDataKinds from '../utils/get-document-data-kinds';
import useEntityId from './use-entity-id';
const {
  logError
} = getLogger({
  location: 'use-decrypt-controls'
});
type DecryptStep = 'idle' | 'selecting-fields' | 'selecting-reason';
type DecryptRequest = {
  entityId: string;
  fields: DataIdentifier[];
};
type DecryptControlsStoreState = {
  step: DecryptStep;
  fields: DataIdentifier[];
  // Only track decrypted fields for the current entity (cleared when entity changes)
  currentEntityId: string | null;
  decryptedFields: DataIdentifier[];
  decryptedDocumentKinds: `${DocumentKind}-${number}`[];
  // One entry per request, so one that finishes doesn't clear fields another is still decrypting.
  pendingRequests: DecryptRequest[];
};
const initialState: DecryptControlsStoreState = {
  step: 'idle',
  fields: [],
  currentEntityId: null,
  decryptedFields: [],
  decryptedDocumentKinds: [],
  pendingRequests: []
};
const useDecryptControlsStore = create<DecryptControlsStoreState>(() => initialState);
const forgetDecryptedFields = (entityId: string, fields: DataIdentifier[]) => {
  useDecryptControlsStore.setState(state => state.currentEntityId === entityId ? {
    decryptedFields: state.decryptedFields.filter(field => !fields.includes(field))
  } : state);
};
const useDecryptControls = () => {
  const store = useDecryptControlsStore();
  const set = useDecryptControlsStore.setState;
  const get = useDecryptControlsStore.getState;
  const showErrorToast = useRequestErrorToast();
  const entityId = useEntityId();

  // Clear decrypted fields when entity changes to ensure the store doesn't contain a previous entity's fields when auto-decryption is triggered.
  // This is also reset in DecryptForm on mount, but useEntityInitialData (where auto-decryption is triggered) may run before DecryptForm resets.
  useEffect(() => {
    if (entityId && store.currentEntityId !== entityId) {
      set({
        currentEntityId: entityId,
        decryptedFields: [],
        decryptedDocumentKinds: []
      });
    }
  }, [entityId, store.currentEntityId, set]);
  const decryptedFields = store.currentEntityId === entityId ? store.decryptedFields : [];
  const decryptedDocumentKinds = store.currentEntityId === entityId ? store.decryptedDocumentKinds : [];
  const fieldsBeingDecrypted = store.pendingRequests.filter(request => request.entityId === entityId).flatMap(request_0 => request_0.fields);
  const isDecrypting = (fields: DataIdentifier[]) => fields.some(field => fieldsBeingDecrypted.includes(field));
  const defer = () => {
    return new Promise(resolve => setTimeout(resolve, 0));
  };
  const startToSelectFields = () => {
    set({
      step: 'selecting-fields'
    });
  };
  const selectAllFields = async () => {
    const documentKinds = Array.from(document.querySelectorAll<HTMLInputElement>('input[name="documentKinds"]'));
    const fields_0 = Array.from(document.querySelectorAll<HTMLInputElement>('input[name="fields"]'));
    fields_0.forEach(checkbox => {
      checkbox.click();
    });
    for (const checkbox_0 of documentKinds) {
      checkbox_0.click();
      await defer();
    }
  };
  const startToDecryptAll = async () => {
    await selectAllFields();
    selectReason();
  };
  const selectReason = async () => {
    set({
      step: 'selecting-reason'
    });
  };
  const cancel = () => {
    set({
      step: 'idle'
    });
  };
  const reset = () => {
    // The form can mount after auto-decrypt starts; only request completion clears its loading state.
    set(state => ({
      ...initialState,
      pendingRequests: state.pendingRequests
    }));
  };
  const getDisFromDocuments = (documents: Document[], vaultData: VaultType) => {
    const imageDis = documents.flatMap(d => d.uploads.map(getUploadImageKey));
    const completedVersionDis = documents.flatMap(({
      completedVersion,
      kind,
      sambaActivityHistoryCompletedVersion
    }) => {
      if (!completedVersion) {
        return [];
      }
      const documentDataKinds = getDocumentDataKinds(vaultData.dataKinds, kind);
      return documentDataKinds.map(di => {
        const isSambaActivityDI = di.includes('samba_activity_history_response');
        const useSambaVersion = isSambaActivityDI && sambaActivityHistoryCompletedVersion;
        const version = useSambaVersion ? sambaActivityHistoryCompletedVersion : completedVersion;
        return `${di}:${version}` as DataIdentifier;
      });
    });
    return [...new Set([...imageDis, ...completedVersionDis])];
  };
  const getDocumentKindsFromFields = (fields_1: DataIdentifier[]) => {
    const documentKinds_0 = fields_1.filter(field_0 => field_0.startsWith('document.')).map(field_1 => {
      const parts = field_1.split('.');
      const kind_0 = parts[1];
      const version_0 = field_1.split(':')[1];
      return `${kind_0}-${version_0}` as `${DocumentKind}-${number}`;
    });
    return [...new Set(documentKinds_0)];
  };
  const decrypt = async ({
    entity,
    fields: fields_2,
    reason
  }: {
    entity: Entity;
    fields: DataIdentifier[];
    reason: string;
  }) => {
    const entityId_0 = entity.id;
    const state_0 = get();
    // Only use decrypted fields if they're for the current entity
    const currentDecryptedFields = state_0.currentEntityId === entityId_0 ? state_0.decryptedFields : [];
    const currentDecryptedDocumentKinds = state_0.currentEntityId === entityId_0 ? state_0.decryptedDocumentKinds : [];
    const fieldsToDecrypt = fields_2.filter(field_2 => !currentDecryptedFields.includes(field_2));

    // The edit dialog reads us_tax_id, which needs its own decryption and permission even when ssn9/itin is allowed.
    const canDecryptUsTaxId = entity.data.some(attr => attr.identifier === 'id.us_tax_id' && attr.isDecryptable);
    const hasSsnOrItin = fieldsToDecrypt.some(field_3 => field_3 === 'id.ssn9' || field_3 === 'id.itin');
    if (canDecryptUsTaxId && hasSsnOrItin && !fieldsToDecrypt.includes('id.us_tax_id')) {
      fieldsToDecrypt.push('id.us_tax_id');
    }
    const request_1: DecryptRequest = {
      entityId: entityId_0,
      fields: fieldsToDecrypt
    };
    set(state_1 => ({
      pendingRequests: [...state_1.pendingRequests, request_1]
    }));
    try {
      const documentKindsToDecrypt = getDocumentKindsFromFields(fieldsToDecrypt).filter(kind_1 => !currentDecryptedDocumentKinds.includes(kind_1));
      const response = await postEntitiesByFpIdVaultDecrypt({
        path: {
          fpId: entityId_0
        },
        body: {
          fields: fieldsToDecrypt,
          reason
        }
      });
      set(state_3 => {
        if (state_3.currentEntityId !== entityId_0) {
          return state_3;
        }
        return {
          step: 'idle',
          decryptedFields: [...state_3.decryptedFields, ...fieldsToDecrypt],
          decryptedDocumentKinds: [...state_3.decryptedDocumentKinds, ...documentKindsToDecrypt]
        };
      });
      return response.data;
    } catch (error) {
      logError('Vault decryption error', error, {
        entityId: entityId_0,
        fields: fieldsToDecrypt.join(',')
      });
      set(state_2 => state_2.currentEntityId === entityId_0 ? {
        step: 'selecting-reason'
      } : state_2);
      showErrorToast(error);
      return null;
    } finally {
      // A task later, once the caller has written the response to the vault. Cleared sooner, a field reads as
      // encrypted for a frame between its spinner and its value.
      setTimeout(() => {
        set(state => ({
          pendingRequests: state.pendingRequests.filter(pending => pending !== request_1)
        }));
      });
    }
  };
  return {
    ...store,
    decryptedFields,
    decryptedDocumentKinds,
    isDecrypting,
    startToSelectFields,
    selectAllFields,
    startToDecryptAll,
    selectReason,
    cancel,
    reset,
    getDisFromDocuments,
    decrypt
  };
};
export { forgetDecryptedFields };
export default useDecryptControls;